import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useNavigate, useSearchParams } from "react-router-dom"

import { axiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import PasswordTextField from "../../../common/components/PasswordTextField"
import LoadingPaper from "../components/LoadingPaper"
import PageTitle from "../components/PageTitle"
import { sessionAccountReceived } from "../reducer"
import {
  INVITE_UNAVAILABLE_MESSAGE,
  INVITE_UNAVAILABLE_TITLE,
  parseInviteError,
} from "./inviteStatus"

export default function AcceptInvite() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(true)
  const [invite, setInvite] = useState(null)
  const [error, setError] = useState(null)
  const [inviteUnavailable, setInviteUnavailable] = useState(false)
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [password1, setPassword1] = useState("")
  const [password2, setPassword2] = useState("")

  function applyInviteError(data, fallback) {
    const { unavailable, message } = parseInviteError(data, fallback)
    setInviteUnavailable(unavailable)
    setError(unavailable ? null : message)
    setInvite(null)
  }

  useEffect(() => {
    if (!token) {
      setInviteUnavailable(true)
      setIsLoading(false)
      return
    }
    axiosClient
      .get("/users/api/accept-invite/", { params: { token } })
      .then(({ data }) => {
        setInviteUnavailable(false)
        setError(null)
        setInvite(data)
        setFirstName(data.first_name || "")
        setLastName(data.last_name || "")
      })
      .catch(({ response }) => {
        applyInviteError(response?.data, "Invalid invitation link.")
      })
      .finally(() => setIsLoading(false))
  }, [token])

  function handleAcceptNewUser() {
    setIsLoading(true)
    setError(null)
    axiosClient
      .post("/users/api/accept-invite/accept/", {
        token,
        first_name: firstName,
        last_name: lastName,
        password_1: password1,
        password_2: password2,
      })
      .then(({ data }) => {
        dispatch(sessionAccountReceived(data))
        navigate("/")
      })
      .catch(({ response }) => {
        applyInviteError(response?.data, "Could not accept invitation.")
      })
      .finally(() => setIsLoading(false))
  }

  if (isLoading && !invite && !inviteUnavailable) {
    return (
      <>
        <PageTitle text="Accept invitation" />
        <LoadingPaper isLoading />
      </>
    )
  }

  if (inviteUnavailable) {
    return (
      <>
        <PageTitle text={INVITE_UNAVAILABLE_TITLE} />
        <LoadingPaper>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <EventBusyOutlinedIcon color="warning" fontSize="large" />
          </Box>
          <Typography variant="body1" sx={{ mt: 2, textAlign: "center" }}>
            {INVITE_UNAVAILABLE_MESSAGE}
          </Typography>
        </LoadingPaper>
      </>
    )
  }

  return (
    <>
      <PageTitle text="Accept invitation" />
      <LoadingPaper isLoading={isLoading}>
        <Column sx={{ gap: 2 }}>
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
          {invite && (
            <>
              <Typography variant="body1">
                <strong>{invite.owner_name}</strong> invited you to access their
                OneBase account.
              </Typography>
              <TextField
                label="Email"
                value={invite.email}
                disabled
                fullWidth
              />
              {!invite.is_existing_user && (
                <>
                  <TextField
                    label="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    fullWidth
                  />
                  <PasswordTextField
                    label="Password"
                    value={password1}
                    onChange={(e) => setPassword1(e.target.value)}
                  />
                  <PasswordTextField
                    label="Confirm password"
                    value={password2}
                    onChange={(e) => setPassword2(e.target.value)}
                  />
                  <Button variant="contained" onClick={handleAcceptNewUser}>
                    Create account & accept
                  </Button>
                </>
              )}
              {invite.is_existing_user && (
                <Button
                  variant="contained"
                  onClick={() =>
                    navigate(
                      `/user/log-in/?next=/users/accept-invite/?token=${token}`
                    )
                  }
                >
                  Sign in to accept
                </Button>
              )}
            </>
          )}
        </Column>
      </LoadingPaper>
    </>
  )
}
